import React, { useState, useEffect, useRef } from 'react';
import './AccessibilityWidget.css';

const DEFAULTS = {
  fontSize: 0,        // 0 = normal, 1 = +3px, 2 = +6px, 3 = +9px
  dyslexia: false,
  highContrast: false,
  reduceMotion: false,
  lineSpacing: false,
};

const FONT_OFFSETS = [0, 3, 6, 9];

function AccessibilityWidget() {
  const [open, setOpen] = useState(false);
  const [prefs, setPrefs] = useState(() => {
    try {
      const saved = localStorage.getItem('a11y_prefs');
      return saved ? { ...DEFAULTS, ...JSON.parse(saved) } : DEFAULTS;
    } catch {
      return DEFAULTS;
    }
  });

  const triggerRef = useRef(null);
  const panelRef = useRef(null);

  // Apply prefs to DOM
  useEffect(() => {
    const offset = FONT_OFFSETS[prefs.fontSize] ?? 0;
    document.documentElement.style.setProperty('--a11y-font-offset', `${offset}px`);
    document.body.classList.toggle('a11y-dyslexia', prefs.dyslexia);
    document.body.classList.toggle('a11y-high-contrast', prefs.highContrast);
    document.body.classList.toggle('a11y-reduce-motion', prefs.reduceMotion);
    document.body.classList.toggle('a11y-spacing', prefs.lineSpacing);
    try {
      localStorage.setItem('a11y_prefs', JSON.stringify(prefs));
    } catch {}
  }, [prefs]);

  // Close on Escape
  useEffect(() => {
    if (!open) return;
    const handler = (e) => {
      if (e.key === 'Escape') {
        setOpen(false);
        triggerRef.current?.focus();
      }
    };
    document.addEventListener('keydown', handler);
    return () => document.removeEventListener('keydown', handler);
  }, [open]);

  // Close on outside click
  useEffect(() => {
    if (!open) return;
    const handler = (e) => {
      if (panelRef.current && !panelRef.current.contains(e.target) && e.target !== triggerRef.current) {
        setOpen(false);
      }
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [open]);

  const toggle = (key) => setPrefs(p => ({ ...p, [key]: !p[key] }));

  const changeFontSize = (dir) => {
    setPrefs(p => ({
      ...p,
      fontSize: Math.max(0, Math.min(3, p.fontSize + dir)),
    }));
  };

  const reset = () => {
    setPrefs(DEFAULTS);
  };

  const isModified = JSON.stringify(prefs) !== JSON.stringify(DEFAULTS);

  return (
    <div className="a11y-widget">
      <button
        ref={triggerRef}
        className={`a11y-widget__trigger ${isModified ? 'a11y-widget__trigger--active' : ''}`}
        onClick={() => setOpen(o => !o)}
        aria-label="Options d'accessibilité"
        aria-expanded={open}
        aria-haspopup="dialog"
        title="Accessibilité"
      >
        <i className="fa-solid fa-universal-access" aria-hidden="true"></i>
      </button>

      {open && (
        <div
          ref={panelRef}
          className="a11y-widget__panel"
          role="dialog"
          aria-label="Options d'accessibilité"
          aria-modal="false"
        >
          <div className="a11y-widget__header">
            <i className="fa-solid fa-universal-access" aria-hidden="true"></i>
            <span>Accessibilité</span>
            <button
              className="a11y-widget__close"
              onClick={() => { setOpen(false); triggerRef.current?.focus(); }}
              aria-label="Fermer le panneau"
            >
              <i className="fa-solid fa-xmark" aria-hidden="true"></i>
            </button>
          </div>

          {/* Font size */}
          <div className="a11y-widget__section">
            <span className="a11y-widget__section-label">
              <i className="fa-solid fa-text-height" aria-hidden="true"></i>
              Taille du texte
            </span>
            <div className="a11y-widget__font-controls">
              <button
                className="a11y-widget__font-btn"
                onClick={() => changeFontSize(-1)}
                disabled={prefs.fontSize === 0}
                aria-label="Réduire la taille du texte"
              >
                A−
              </button>
              <span className="a11y-widget__font-level" aria-live="polite" aria-label={`Niveau ${prefs.fontSize + 1} sur 4`}>
                {['Normal', 'Grand', 'Très grand', 'Maximum'][prefs.fontSize]}
              </span>
              <button
                className="a11y-widget__font-btn"
                onClick={() => changeFontSize(1)}
                disabled={prefs.fontSize === 3}
                aria-label="Agrandir la taille du texte"
              >
                A+
              </button>
            </div>
          </div>

          {/* Dyslexia font */}
          <div className="a11y-widget__section">
            <label className="a11y-widget__section-label" htmlFor="a11y-dyslexia">
              <i className="fa-solid fa-font" aria-hidden="true"></i>
              Police lisibilité
            </label>
            <button
              id="a11y-dyslexia"
              role="switch"
              aria-checked={prefs.dyslexia}
              onClick={() => toggle('dyslexia')}
              className={`a11y-widget__toggle ${prefs.dyslexia ? 'a11y-widget__toggle--on' : ''}`}
              aria-label={`Police lisibilité : ${prefs.dyslexia ? 'activée' : 'désactivée'}`}
            >
              <span className="a11y-widget__toggle-thumb" aria-hidden="true"></span>
            </button>
          </div>

          {/* High contrast */}
          <div className="a11y-widget__section">
            <label className="a11y-widget__section-label" htmlFor="a11y-contrast">
              <i className="fa-solid fa-circle-half-stroke" aria-hidden="true"></i>
              Contraste élevé
            </label>
            <button
              id="a11y-contrast"
              role="switch"
              aria-checked={prefs.highContrast}
              onClick={() => toggle('highContrast')}
              className={`a11y-widget__toggle ${prefs.highContrast ? 'a11y-widget__toggle--on' : ''}`}
              aria-label={`Contraste élevé : ${prefs.highContrast ? 'activé' : 'désactivé'}`}
            >
              <span className="a11y-widget__toggle-thumb" aria-hidden="true"></span>
            </button>
          </div>

          {/* Reduce motion */}
          <div className="a11y-widget__section">
            <label className="a11y-widget__section-label" htmlFor="a11y-motion">
              <i className="fa-solid fa-wind" aria-hidden="true"></i>
              Moins d'animations
            </label>
            <button
              id="a11y-motion"
              role="switch"
              aria-checked={prefs.reduceMotion}
              onClick={() => toggle('reduceMotion')}
              className={`a11y-widget__toggle ${prefs.reduceMotion ? 'a11y-widget__toggle--on' : ''}`}
              aria-label={`Réduire les animations : ${prefs.reduceMotion ? 'activé' : 'désactivé'}`}
            >
              <span className="a11y-widget__toggle-thumb" aria-hidden="true"></span>
            </button>
          </div>

          {/* Line spacing */}
          <div className="a11y-widget__section">
            <label className="a11y-widget__section-label" htmlFor="a11y-spacing">
              <i className="fa-solid fa-align-left" aria-hidden="true"></i>
              Grand espacement
            </label>
            <button
              id="a11y-spacing"
              role="switch"
              aria-checked={prefs.lineSpacing}
              onClick={() => toggle('lineSpacing')}
              className={`a11y-widget__toggle ${prefs.lineSpacing ? 'a11y-widget__toggle--on' : ''}`}
              aria-label={`Grand espacement : ${prefs.lineSpacing ? 'activé' : 'désactivé'}`}
            >
              <span className="a11y-widget__toggle-thumb" aria-hidden="true"></span>
            </button>
          </div>

          {isModified && (
            <button className="a11y-widget__reset" onClick={reset}>
              <i className="fa-solid fa-rotate-left" aria-hidden="true"></i>
              Réinitialiser
            </button>
          )}
        </div>
      )}
    </div>
  );
}

export default AccessibilityWidget;
